import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import api from '../../utils/axiosConfig';
import { getImageUrl } from '../../utils/imageUtils';

interface Project {
    _id: string;
    title: string;
    slug: string;
    subtitle?: string;
    category: string;
    shortDescription: string;
    image?: string;
    logo?: string;
    banner?: string;
    color: string;
}

interface SliderImage {
    _id: string;
    title: string;
    image: string;
    link?: string;
}

const LandingPage: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentProjectSlide, setCurrentProjectSlide] = useState(0);
    const [isDesktop, setIsDesktop] = useState(false);
    const [projects, setProjects] = useState<Project[]>([]);
    const [slides, setSlides] = useState<SliderImage[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [loadingSliders, setLoadingSliders] = useState(true);

    useEffect(() => {
        fetchProjects();
        fetchSliders();
    }, []);

    useEffect(() => {
        const checkDesktop = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };
        checkDesktop();
        window.addEventListener('resize', checkDesktop);
        return () => window.removeEventListener('resize', checkDesktop);
    }, []);

    const fetchProjects = async () => {
        try {
            setLoadingProjects(true);
            const response = await api.get('/projects');
            setProjects(response.data.data || []);
        } catch (error: any) {
            console.error('Failed to fetch projects:', error);
        } finally {
            setLoadingProjects(false);
        }
    };

    const fetchSliders = async () => {
        try {
            setLoadingSliders(true);
            const response = await api.get('/sliders');
            const sliderData = response.data.data || [];
            if (sliderData.length > 0) {
                setSlides(sliderData);
            } else {
                // Fallback to default slides if no sliders in database
                setSlides([
                    {
                        _id: '1',
                        title: "DIGITAL MARKETING",
                        image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
                    },
                    {
                        _id: '2',
                        title: "MARKETING STRATEGY",
                        image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop"
                    },
                    {
                        _id: '3',
                        title: "BUSINESS GROWTH",
                        image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=600&fit=crop"
                    }
                ]);
            }
        } catch (error: any) {
            console.error('Failed to fetch sliders:', error);
            // Fallback to default slides on error
            setSlides([
                {
                    _id: '1',
                    title: "DIGITAL MARKETING",
                    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
                },
                {
                    _id: '2',
                    title: "MARKETING STRATEGY",
                    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop"
                },
                {
                    _id: '3',
                    title: "BUSINESS GROWTH",
                    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=600&fit=crop"
                }
            ]);
        } finally {
            setLoadingSliders(false);
        }
    };

    useEffect(() => {
        if (slides.length > 0) {
            const timer = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % slides.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [slides.length]);

    const nextSlide = () => {
        if (slides.length > 0) {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }
    };

    const prevSlide = () => {
        if (slides.length > 0) {
            setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
        }
    };

    const goToSlide = (index: number) => {
        setCurrentSlide(index);
    };

    const nextProjectSlide = () => {
        const cardsPerView = isDesktop ? 3 : 1;
        const maxSlides = Math.max(0, projects.length - cardsPerView);
        setCurrentProjectSlide((prev) => (prev >= maxSlides ? 0 : prev + 1));
    };

    const prevProjectSlide = () => {
        const cardsPerView = isDesktop ? 3 : 1;
        const maxSlides = Math.max(0, projects.length - cardsPerView);
        setCurrentProjectSlide((prev) => (prev <= 0 ? maxSlides : prev - 1));
    };

    const cardsPerView = isDesktop ? 3 : 1;

    const getProjectImage = (project: Project) => {
        if (project.image) return getImageUrl(project.image);
        if (project.banner) return getImageUrl(project.banner);
        if (project.logo) return getImageUrl(project.logo);
        if (project.color === 'blue') {
            return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop';
        } else if (project.color === 'orange') {
            return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop';
        }
        return 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop';
    };

    const getBadgeColor = (color: string) => {
        switch (color) {
            case 'blue': return 'bg-gradient-to-r from-[#3B82F6] to-blue-600';
            case 'orange': return 'bg-gradient-to-r from-[#F59E0B] to-orange-600';
            case 'green': return 'bg-gradient-to-r from-green-500 to-green-600';
            case 'purple': return 'bg-gradient-to-r from-purple-500 to-purple-600';
            default: return 'bg-gradient-to-r from-gray-500 to-gray-600';
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <PublicHeader />

            <section className="pt-32 pb-24 relative overflow-hidden bg-gradient-to-br from-[#F8FAFC] via-white to-blue-50/30">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -left-40 w-80 h-80 bg-[#F59E0B]/10 rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-8">
                                <span className="w-2 h-2 bg-[#3B82F6] rounded-full mr-2 animate-pulse"></span>
                                <span className="text-sm font-medium text-[#3B82F6]">Leading Marketing Agency</span>
                            </div>
                            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-8 leading-[1.1] text-left">
                                <span className="text-[#3B3B3B] block">Transform Your</span>
                                <span className="text-[#3B82F6] block">Business</span>
                                <span className="text-[#3B3B3B] inline"> with </span>
                                <span className="text-[#F59E0B] inline">We4Task</span>
                            </h1>
                            <p className="text-xl sm:text-2xl text-gray-600 mb-12 leading-relaxed text-left max-w-3xl">
                                We're a leading marketing agency dedicated to helping businesses grow through innovative strategies and exceptional service.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/contact"
                                    className="group inline-flex items-center justify-center px-8 py-4 bg-[#3B82F6] text-white rounded-xl hover:bg-blue-700 hover:shadow-2xl transition-all duration-300 font-semibold text-base shadow-lg hover:-translate-y-1"
                                >
                                    Get Started
                                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </Link>
                                <Link
                                    to="/services"
                                    className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:border-[#3B82F6] hover:text-[#3B82F6] transition-all duration-300 font-semibold text-base hover:shadow-lg"
                                >
                                    Our Services
                                </Link>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white">
                                <div className="relative h-[500px] lg:h-[600px] overflow-hidden">
                                    {loadingSliders ? (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                            <div className="text-center">
                                                <div className="spinner mx-auto"></div>
                                                <p className="mt-4 text-gray-600">Loading...</p>
                                            </div>
                                        </div>
                                    ) : slides.length > 0 ? (
                                        slides.map((slide, index) => (
                                            <div
                                                key={slide._id}
                                                className={`absolute inset-0 transition-opacity duration-500 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                                                    }`}
                                            >
                                                {slide.link ? (
                                                    <Link to={slide.link}>
                                                        <img
                                                            src={getImageUrl(slide.image)}
                                                            alt={slide.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </Link>
                                                ) : (
                                                    <img
                                                        src={getImageUrl(slide.image)}
                                                        alt={slide.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                            <p className="text-gray-600">No slider images available</p>
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={prevSlide}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-10"
                                    aria-label="Previous slide"
                                >
                                    <svg className="w-5 h-5 text-[#3B3B3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={nextSlide}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-10"
                                    aria-label="Next slide"
                                >
                                    <svg className="w-5 h-5 text-[#3B3B3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                                {slides.length > 1 && (
                                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                                        {slides.map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => goToSlide(index)}
                                                className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
                                                    }`}
                                                aria-label={`Go to slide ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-4">Why Choose We4Task?</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            We deliver exceptional results through our core values
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="p-8 rounded-2xl bg-gradient-to-b from-blue-300 to-blue-100">
                            <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Excellent Customer Service</h3>
                            <p className="text-base text-[#3B3B3B] leading-relaxed">
                                As a marketing agency we have been there for Excellent Customer Service consistently provide friendly, responsive, and helpful customer support
                            </p>
                        </div>
                        <div className="p-8 rounded-2xl bg-gradient-to-b from-orange-300 to-orange-200">
                            <div className="w-14 h-14 bg-orange-600 rounded-full flex items-center justify-center mb-6">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Reputation and Trustworthiness</h3>
                            <p className="text-base text-[#3B3B3B] leading-relaxed">
                                We4task has build a strong reputation for reliability, trustworthiness, we have build from our services and offerings to our client
                            </p>
                        </div>
                        <div className="p-8 rounded-2xl bg-gradient-to-b from-purple-300 to-purple-200">
                            <div className="w-14 h-14 bg-purple-600 rounded-full flex items-center justify-center mb-6">
                                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Positive Brand Identity</h3>
                            <p className="text-base text-[#3B3B3B] leading-relaxed">
                                We as a marketing agency we build strong and recognizable brand identity can influence customers' decision-making. Our team will boost your brand in well-positioned, communicates a clear value proposition, and connects with customers on an emotional level.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-orange-50/30 pointer-events-none"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-4">Our Latest Projects</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Discover our recent successful campaigns and transformations
                        </p>
                    </div>

                    {/* Projects Slider */}
                    {loadingProjects ? (
                        <div className="text-center py-12">
                            <div className="spinner mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading projects...</p>
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-600">No projects available at the moment.</p>
                        </div>
                    ) : (
                        <div className="relative">
                            <div className="overflow-hidden">
                                <div
                                    className="flex transition-transform duration-500 ease-in-out"
                                    style={{
                                        transform: `translateX(-${currentProjectSlide * (100 / cardsPerView)}%)`
                                    }}
                                >
                                    {projects.map((project: Project) => (
                                        <div
                                            key={project._id}
                                            className={`${isDesktop ? 'w-1/3' : 'w-full'} flex-shrink-0 px-4`}
                                        >
                                            <div className="group bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden h-full">
                                                <div className="relative h-48 overflow-hidden">
                                                    <img
                                                        src={getProjectImage(project)}
                                                        alt={project.title}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                    />
                                                    <div className="absolute top-4 left-4">
                                                        <span className={`inline-block px-3 py-1 ${getBadgeColor(project.color)} text-white rounded-full text-xs font-semibold shadow-lg`}>
                                                            {project.category}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="p-6">
                                                    <h3 className="text-xl font-bold text-[#3B3B3B] mb-3 group-hover:text-[#3B82F6] transition-colors">
                                                        {project.title}
                                                    </h3>
                                                    <p className="text-base text-gray-600 mb-6 leading-relaxed line-clamp-3">
                                                        {project.shortDescription}
                                                    </p>
                                                    <Link
                                                        to={`/projects/${project.slug}`}
                                                        className="inline-flex items-center px-6 py-3 bg-[#3B82F6] text-white rounded-xl hover:bg-blue-700 transition-all duration-300 font-semibold text-sm group-hover:gap-2"
                                                    >
                                                        View More
                                                        <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                        </svg>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Navigation Buttons */}
                            {projects.length > cardsPerView && (
                                <>
                                    <button
                                        onClick={prevProjectSlide}
                                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all z-10"
                                        aria-label="Previous project"
                                    >
                                        <svg className="w-5 h-5 text-[#3B3B3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={nextProjectSlide}
                                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all z-10"
                                        aria-label="Next project"
                                    >
                                        <svg className="w-5 h-5 text-[#3B3B3B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </>
                            )}

                            {/* Dots Indicator */}
                            {projects.length > cardsPerView && (
                                <div className="flex justify-center gap-2 mt-8">
                                    {Array.from({ length: Math.ceil(projects.length / cardsPerView) }).map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentProjectSlide(index)}
                                            className={`w-2 h-2 rounded-full transition-all ${currentProjectSlide === index ? 'bg-[#3B82F6] w-8' : 'bg-gray-300'
                                                }`}
                                            aria-label={`Go to slide ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="text-center mt-12">
                        <Link
                            to="/projects"
                            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold text-base"
                        >
                            View All Projects
                            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>


            <PublicFooter />
        </div>
    );
};

export default LandingPage;
